/**
 * Earth — cartoon-style rotating planet on the right side. Self-rotates,
 * has continents (procedural texture but cel-shaded), atmosphere halo,
 * and a face sprite that emotes with game state.
 *
 * Static cannon body: meteors collide with the surface and stack.
 */

import {
  Body,
  Sphere as CannonSphere,
  Vec3,
} from "cannon-es";
import {
  Color,
  Group,
  Mesh,
  MeshToonMaterial,
  ShaderMaterial,
  SphereGeometry,
} from "three";

import { createToonOutline } from "../render/toon-outline.ts";
import { createToonAtmosphere } from "../shaders/toon-atmosphere.ts";
import { createToonGradient } from "../shaders/toon-gradient.ts";
import { createEarthFace, type EarthFace, type EarthEmotion } from "./earth-face.ts";
import { paintEarthSurface } from "./earth-surface-paint.ts";

const EARTH_RADIUS = 4.6;

const STRESS_HEALTHY = new Color(0x88c8ff);
const STRESS_AMBER = new Color(0xff9c5b);
const STRESS_CRITICAL = new Color(0xff3b3b);

export interface EarthOptions {
  position: { x: number; y: number; z: number };
}

export interface Earth {
  group: Group;
  position: { x: number; y: number; z: number };
  radius: number;
  body: Body;
  setEmotion(emotion: EarthEmotion): void;
  /**
   * Pipe Earth's structural integrity into visuals.
   * @param ratio 1.0 = healthy, 0.0 = collapsed. Drives atmosphere
   * color shift (cyan → amber → red) and surface wobble intensity.
   */
  setStress(ratio: number): void;
  tick(dt: number): void;
  detach(): void;
}

export function createEarth(options: EarthOptions): Earth {
  const radius = EARTH_RADIUS;

  const group = new Group();
  group.name = "earth";
  group.position.set(options.position.x, options.position.y, options.position.z);

  const surface = new Mesh(
    new SphereGeometry(radius, 96, 64),
    new MeshToonMaterial({
      map: paintEarthSurface(),
      gradientMap: createToonGradient(4),
    }),
  );
  surface.name = "earth-surface";
  surface.rotation.z = 0.41;

  // Cel outline silhouette — slightly larger shell. The cannon body
  // below uses the SAME outer radius so meteors stop precisely at the
  // visual silhouette edge instead of clipping into the dark border.
  const OUTLINE_SCALE = 1.035;
  const outline = createToonOutline({ planetRadius: radius, scale: OUTLINE_SCALE });
  surface.add(outline);

  const atmosphere = createToonAtmosphere({
    planetRadius: radius,
    color: 0x88c8ff,
    intensity: 1.4,
    bandCount: 3,
    shellScale: 1.12,
  });
  const atmosphereMaterial = atmosphere.material as ShaderMaterial;

  const face: EarthFace = createEarthFace(radius);

  group.add(surface, atmosphere, face.sprite);

  // Body radius matches the visible silhouette edge (mesh + outline)
  // so meteors collide right where the eye expects.
  const collisionRadius = radius * OUTLINE_SCALE;
  const body = new Body({
    mass: 0,
    shape: new CannonSphere(collisionRadius),
    position: new Vec3(options.position.x, options.position.y, options.position.z),
    type: Body.STATIC,
  });

  let elapsed = 0;
  // 1.0 = full integrity, 0.0 = collapsed. Mutated by setStress.
  let stressHealth = 1;
  const tickColor = new Color();
  const tick = (dt: number): void => {
    elapsed += dt;
    // Surface wobble — minimal at full integrity, increasingly violent
    // as Earth approaches collapse. Mixes a low-amplitude sine into the
    // base rotation so the planet visibly trembles under stress.
    const wobble = (1 - stressHealth) * 0.18 * Math.sin(elapsed * 14);
    surface.rotation.y = elapsed * 0.07 + wobble;
    surface.rotation.x = (1 - stressHealth) * 0.05 * Math.sin(elapsed * 18);
    // Atmosphere color drifts cyan → amber → red as integrity drops.
    if (stressHealth > 0.5) {
      tickColor.copy(STRESS_HEALTHY).lerp(STRESS_AMBER, (1 - stressHealth) * 2);
    } else {
      tickColor.copy(STRESS_AMBER).lerp(STRESS_CRITICAL, (0.5 - stressHealth) * 2);
    }
    atmosphereMaterial.uniforms.uColor!.value.copy(tickColor);
    // Atmosphere intensity pulses faster + brighter when critical.
    const pulseRate = 1.2 + (1 - stressHealth) * 6;
    const pulse = 1 + (1 - stressHealth) * 0.15 * Math.sin(elapsed * pulseRate);
    atmosphereMaterial.uniforms.uIntensity!.value = 1.4 * pulse;
    face.tick(dt);
  };

  const setStress = (ratio: number): void => {
    stressHealth = Math.max(0, Math.min(1, ratio));
  };

  const detach = (): void => {
    (surface.geometry as SphereGeometry).dispose();
    (surface.material as MeshToonMaterial).dispose();
    (outline.geometry as SphereGeometry).dispose();
    (outline.material as { dispose(): void }).dispose();
    (atmosphere.geometry as { dispose(): void }).dispose();
    (atmosphere.material as { dispose(): void }).dispose();
    face.detach();
    group.removeFromParent();
  };

  return {
    group,
    position: { ...options.position },
    radius: collisionRadius,
    body,
    setEmotion: (e) => face.setEmotion(e),
    setStress,
    tick,
    detach,
  };
}

export const __testing = { EARTH_RADIUS };
